import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { parseBillingOverview, parseBillingOverviews, type BillingOverview } from "@/lib/billing-overview";
import { useCreditsChanged } from "@/lib/credits-events";

export function useBillingOverview(orgId: string | null) {
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [loading, setLoading] = useState(Boolean(orgId));
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!orgId) {
      setOverview(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    supabase.rpc("billing_overview", { p_org_id: orgId }).then(({ data, error: rpcError }) => {
      if (cancelled) return;
      if (rpcError) {
        console.error("[useBillingOverview]", rpcError.message);
        setError(rpcError.message);
        setOverview(null);
      } else {
        setOverview(parseBillingOverview(data));
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [orgId, version]);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  useCreditsChanged(orgId, refresh);

  return { overview, loading, error, refresh };
}

export function useBillingOverviews(orgIds: string[]) {
  const key = useMemo(() => [...orgIds].sort().join(","), [orgIds]);
  const [overviews, setOverviews] = useState<Map<string, BillingOverview>>(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!key) {
      setOverviews(new Map());
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    supabase.rpc("billing_overviews", { p_org_ids: key.split(",") }).then(({ data, error: rpcError }) => {
      if (cancelled) return;
      if (rpcError) console.error("[useBillingOverviews]", rpcError.message);
      setOverviews(rpcError ? new Map() : parseBillingOverviews(data));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [key]);

  return { overviews, loading };
}
